// Sales Pipeline report — pre-sales output (opportunities / presentations /
// estimates) created in the range. Click a box to list them all
// (Stephanie 2026-10-16).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ReportShell, useReportRange } from "@/components/ReportShell";
import { ReportRowsTable, ReportTile, type ReportList } from "@/components/ReportRowsTable";

interface Report {
  range: { from: string; to: string };
  presentations: { count: number };
  estimates: { count: number; total: string };
  opportunities: { count: number; budgetTotal: string };
  fetchedAt: string;
}

const TITLES: Partial<Record<ReportList, string>> = {
  opportunities: "Opportunities opened",
  presentations: "Presentations created",
  estimates: "Estimates created",
};

export function ReportSalesPipelinePage() {
  const { preset, setPreset, range, setCustom, repId, setRepId } = useReportRange();
  const [list, setList] = useState<ReportList>("opportunities");
  const rep = repId ? `&rep_id=${repId}` : "";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "sales-pipeline", range.from, range.to, repId],
    queryFn: () => api<Report>(`/reports/sales-pipeline?from=${range.from}&to=${range.to}${rep}`),
  });

  return (
    <ReportShell
      title="Sales Pipeline"
      subtitle="Pre-sales output: what's getting opened and quoted. Click a box to see the list."
      preset={preset}
      onPresetChange={setPreset}
      range={range}
      onCustomRange={setCustom}
      repId={repId}
      onRepChange={setRepId}
      fetchedAt={data?.fetchedAt}
    >
      {isLoading ? (
        <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-300">Loading…</div>
      ) : isError || !data ? (
        <div className="rounded-lg border border-rust-300 bg-rust-50 p-10 text-center text-sm text-rust-600">Couldn't load report.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ReportTile
            label="Opportunities"
            value={data.opportunities.count}
            amount={data.opportunities.budgetTotal}
            amountLabel="Budget"
            sub="Projects still at opportunity, opened in the range"
            selected={list === "opportunities"}
            onSelect={() => setList("opportunities")}
          />
          <ReportTile
            label="Presentations"
            value={data.presentations.count}
            sub="Presentations created in the range"
            selected={list === "presentations"}
            onSelect={() => setList("presentations")}
          />
          <ReportTile
            label="Estimates"
            value={data.estimates.count}
            amount={data.estimates.total}
            amountLabel="Estimate total"
            sub="Estimates created in the range, not yet sales orders"
            selected={list === "estimates"}
            onSelect={() => setList("estimates")}
          />
        </div>
      )}
      <ReportRowsTable list={list} range={range} repId={repId} title={TITLES[list] ?? ""} />
    </ReportShell>
  );
}
